import React, { useState } from "react";
import logger from '../../utils/logger';
import { graphics, getPlayerColor, ChessColor } from './ChessLib';
import { ChessGame, ChessPiece, SquarePosition } from './types';
import './ChessContainer.css';

interface ChessContainerProps {
    game: ChessGame;
    playerId: string | null;
    makeMove: (from: SquarePosition, to: SquarePosition, piece: ChessPiece) => void;
}

interface PlayerMove {
    from: string;
    to: string;
}

function ChessContainer({ game, playerId, makeMove }: ChessContainerProps) {
    const [selectedSquare, setSelectedSquare] = useState<SquarePosition | null>(null);
    const [legalMoves, setLegalMoves] = useState<SquarePosition[]>([]);

    const calculateLegalMoves = (row: number, col: number, piece: string): SquarePosition[] => {
        const moves: SquarePosition[] = [];
        const actualRow = getPlayerColor(game, playerId) === ChessColor.BLACK ? 7 - row : row;
        const pieceType = piece.toUpperCase();
        const isWhite = piece === piece.toUpperCase();
        const playerColor = getPlayerColor(game, playerId);

        // Simple legal move calculation (basic rules, server will validate)
        // This is just for visual highlighting
        for (let r = 0; r < 8; r++) {
            for (let c = 0; c < 8; c++) {
                const targetActualRow = playerColor === ChessColor.BLACK ? 7 - r : r;
                const targetPiece = game.board[targetActualRow][c];

                // Skip if same square
                if (r === row && c === col) continue;

                // Skip if target has piece of same color
                if (targetPiece) {
                    const targetIsWhite = targetPiece === targetPiece.toUpperCase();
                    if (targetIsWhite === isWhite) continue;
                }

                // Basic movement patterns (not checking for checks, pins, etc.)
                let canMove = false;

                switch (pieceType) {
                    case 'P': // Pawn
                        const direction = isWhite ? -1 : 1;
                        const startRow = isWhite ? 6 : 1;
                        // Forward move
                        if (c === col && r === row + direction && !targetPiece) canMove = true;
                        // Double forward from start
                        if (c === col && actualRow === startRow && r === row + 2 * direction && !targetPiece) canMove = true;
                        // Diagonal capture
                        if (Math.abs(c - col) === 1 && r === row + direction && targetPiece) canMove = true;
                        break;

                    case 'R': // Rook
                        if (r === row || c === col) canMove = true;
                        break;

                    case 'N': // Knight
                        const dr = Math.abs(r - row);
                        const dc = Math.abs(c - col);
                        if ((dr === 2 && dc === 1) || (dr === 1 && dc === 2)) canMove = true;
                        break;

                    case 'B': // Bishop
                        if (Math.abs(r - row) === Math.abs(c - col)) canMove = true;
                        break;

                    case 'Q': // Queen
                        if (r === row || c === col || Math.abs(r - row) === Math.abs(c - col)) canMove = true;
                        break;

                    case 'K': // King
                        if (Math.abs(r - row) <= 1 && Math.abs(c - col) <= 1) canMove = true;
                        break;
                }

                if (canMove) {
                    moves.push({ row: r, col: c });
                }
            }
        }

        return moves;
    };

    const handleSquareClick = (rowIndex: number, cellIndex: number): void => {
        // Only allow moves when game is ongoing
        if (game.state !== 'ongoing') {
            return;
        }

        if (selectedSquare) {
            const actualRow = getPlayerColor(game, playerId) === ChessColor.BLACK ? 7 - selectedSquare.row : selectedSquare.row;
            const selectedPiece = game.board[actualRow][selectedSquare.col];
            // Execute the move if it's valid (You can add more validation checks here)
            makeMove(selectedSquare, { row: rowIndex, col: cellIndex }, selectedPiece);
            setSelectedSquare(null);
            setLegalMoves([]);
        } else {
            const actualRow = getPlayerColor(game, playerId) === ChessColor.BLACK ? 7 - rowIndex : rowIndex;
            const selectedPiece = game.board[actualRow][cellIndex];
            if (selectedPiece && ((selectedPiece === selectedPiece.toUpperCase() && getPlayerColor(game, playerId) === ChessColor.WHITE) || (selectedPiece !== selectedPiece.toUpperCase() && getPlayerColor(game, playerId) === ChessColor.BLACK))) {
                logger.debug("setting selected piece", { row: rowIndex, col: cellIndex });
                setSelectedSquare({ row: rowIndex, col: cellIndex });
                setLegalMoves(calculateLegalMoves(rowIndex, cellIndex, selectedPiece));
            }
        }
    };

    // Legacy moves array might not exist in new schema
    const gameMoves = (game.moves as PlayerMove[] | undefined) || [];
    const lastMove = gameMoves.length > 0 ? gameMoves[gameMoves.length - 1] : null;
    const secondLastMove = gameMoves.length > 1 ? gameMoves[gameMoves.length - 2] : null;

    const isLastMoveSquare = (row: number, col: number): boolean => {
        const adjustedRow = getPlayerColor(game, playerId) === ChessColor.BLACK ? 7 - row : row;
        const cellName = `${String.fromCharCode(97 + col)}${8 - adjustedRow}`;
        return lastMove !== null && (cellName === lastMove.from || cellName === lastMove.to);
    };

    const isSecondLastMoveSquare = (row: number, col: number): boolean => {
        const adjustedRow = getPlayerColor(game, playerId) === ChessColor.BLACK ? 7 - row : row;
        const cellName = `${String.fromCharCode(97 + col)}${8 - adjustedRow}`;
        return secondLastMove !== null && (cellName === secondLastMove.from || cellName === secondLastMove.to);
    };

    const lastMoveByCurrentPlayer = gameMoves.length > 0 && getPlayerColor(game, playerId) === (gameMoves.length % 2 === 0 ? ChessColor.BLACK : ChessColor.WHITE);

    const playerColor = getPlayerColor(game, playerId);

    return (
        <>
            <div className="chess-container">
                <div className="row-labels">
                    {(playerColor === ChessColor.BLACK ? ['1', '2', '3', '4', '5', '6', '7', '8'] : ['8', '7', '6', '5', '4', '3', '2', '1']).map(label => (
                        <div key={label} className="row-label">{label}</div>
                    ))}
                </div>
                <div className="chessboard-wrapper">
                    <div className="column-labels">
                        {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map(label => (
                            <div key={label} className="column-label">{label}</div>
                        ))}
                    </div>
                    <div className="chessboard">
                        {(playerColor === ChessColor.BLACK ? game.board.slice().reverse() : game.board).map((row, rowIndex) => (
                            row.map((cell, cellIndex) => {
                                const isDarkSquare = playerColor === ChessColor.WHITE
                                    ? (rowIndex + cellIndex) % 2 !== 0
                                    : (rowIndex + cellIndex) % 2 === 0;

                                const isLegalMove = legalMoves.some(move => move.row === rowIndex && move.col === cellIndex);

                                const classNames = [
                                    'square',
                                    isDarkSquare ? 'black-square' : 'white-square',
                                    selectedSquare && selectedSquare.row === rowIndex && selectedSquare.col === cellIndex ? 'selected' : '',
                                    isLegalMove ? 'legal-move' : '',
                                    isLastMoveSquare(rowIndex, cellIndex) && lastMoveByCurrentPlayer ? 'last-move-self' : '',
                                    isLastMoveSquare(rowIndex, cellIndex) && !lastMoveByCurrentPlayer ? 'last-move-opponent' : '',
                                    isSecondLastMoveSquare(rowIndex, cellIndex) && !lastMoveByCurrentPlayer ? 'second-last-move-self' : '',
                                    isSecondLastMoveSquare(rowIndex, cellIndex) && lastMoveByCurrentPlayer ? 'second-last-move-opponent' : ''
                                ].filter(Boolean).join(' ');

                                return (
                                    <div
                                        key={`${rowIndex}-${cellIndex}`}
                                        className={classNames}
                                        onClick={() => handleSquareClick(rowIndex, cellIndex)}
                                    >
                                        {cell && (
                                            <img
                                                src={graphics[`${cell.toLowerCase()}-${cell === cell.toUpperCase() ? 'white' : 'black'}`]}
                                                alt={cell}
                                                className="piece-img"
                                            />
                                        )}
                                    </div>
                                );
                            })
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}

export default ChessContainer;
